import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Paper,
  Link,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import EventIcon from "@mui/icons-material/Event";
import NotesIcon from "@mui/icons-material/Notes";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import SmsIcon from "@mui/icons-material/Sms";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { PastInteraction } from "./types/businessInsights";
import { formatDate } from "../../../utils";

const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;

interface PastInteractionsModalProps {
  contactId: string | number;
  open: boolean;
  onClose: () => void;
}

export const PastInteractionsModal = ({ contactId, open, onClose }: PastInteractionsModalProps) => {
  const theme = useTheme();
  const [interactions, setInteractions] = useState<PastInteraction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPastInteractions = async () => {
      if (!contactId || !open) return;

      setIsLoading(true);
      setError(null);

      try {
        const { data } = await axios.get<PastInteraction[]>(
          "/api/clientphonebook/contactInteractions/getPastInteractions",
          {
            params: {
              contactId
            }
          }
        );

        setInteractions(data);
      } catch (err) {
        setError("Failed to load past interactions");
        console.error("Error fetching past interactions:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPastInteractions();
  }, [contactId, open]);

  const getInteractionIcon = (typeName: string | null) => {
    switch (typeName?.toLowerCase()) {
      case "phone call":
        return <PhoneIcon sx={{ fontSize: 18 }} />;
      case "text":
        return <SmsIcon sx={{ fontSize: 18 }} />;
      case "email":
        return <EmailIcon sx={{ fontSize: 18 }} />;
      case "event":
        return <EventIcon sx={{ fontSize: 18 }} />;
      case "in person office visit":
        return <MeetingRoomIcon sx={{ fontSize: 18 }} />;
      case "in person encounter":
        return <Diversity3Icon sx={{ fontSize: 18 }} />;
      case "1031 candy":
        return <CardGiftcardIcon sx={{ fontSize: 18 }} />;
      case "voice mail":
        return <RecordVoiceOverIcon sx={{ fontSize: 18 }} />;
      case "simcha":
        return <CelebrationIcon sx={{ fontSize: 18 }} />;
      default:
        return <NotesIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getInteractionColor = (typeName: string | null) => {
    switch (typeName?.toLowerCase()) {
      case "phone call":
        return theme.palette.info.main;
      case "text":
        return theme.palette.info.light;
      case "email":
        return theme.palette.success.main;
      case "event":
        return theme.palette.secondary.main;
      case "in person office visit":
        return theme.palette.primary.main;
      case "in person encounter":
        return theme.palette.primary.light;
      case "1031 candy":
        return "#ff69b4";
      case "voice mail":
        return theme.palette.info.dark;
      case "simcha":
        return "#FFD700";
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "80vh"
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={700}>
            Past Interactions
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {isLoading ? (
          <Box
            sx={{
              p: 4,
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        ) : interactions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <NotesIcon sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
            <Typography variant="body1" color="text.secondary" fontWeight={600}>
              No past interactions recorded
            </Typography>
          </Box>
        ) : (
          <Timeline
            sx={{
              p: 1,
              m: 0,
              "& .MuiTimelineItem-root:before": {
                flex: 0,
                padding: 0
              }
            }}
          >
            {interactions.map((interaction, index) => (
              <TimelineItem key={interaction.id}>
                <TimelineOppositeContent
                  sx={{
                    flex: 0.2,
                    py: 1.5,
                    px: 1,
                    minWidth: 80
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    {interaction.interactionDate
                      ? formatDate(interaction.interactionDate)
                      : formatDate(interaction.createdAt)}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      bgcolor: alpha(getInteractionColor(interaction.type?.name || null), 0.1),
                      color: getInteractionColor(interaction.type?.name || null),
                      border: `2px solid ${getInteractionColor(interaction.type?.name || null)}`,
                      p: 0.75,
                      m: 0
                    }}
                  >
                    {getInteractionIcon(interaction.type?.name || null)}
                  </TimelineDot>
                  {index < interactions.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent sx={{ py: 1.5, px: 1.5 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Stack spacing={1}>
                      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                        <Chip
                          label={interaction.type?.name || "Unknown"}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: alpha(getInteractionColor(interaction.type?.name || null), 0.1),
                            color: getInteractionColor(interaction.type?.name || null),
                            border: `1px solid ${alpha(getInteractionColor(interaction.type?.name || null), 0.3)}`
                          }}
                        />
                        {interaction.interactionCategory && (
                          <Chip
                            label={interaction.interactionCategory}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 500,
                              color: theme.palette.text.secondary,
                              borderColor: theme.palette.divider
                            }}
                          />
                        )}
                      </Box>

                      {interaction.clientCultivator && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PersonIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                          <Typography variant="caption" color="text.secondary">
                            {interaction.clientCultivator.firstName} {interaction.clientCultivator.lastName}
                          </Typography>
                        </Box>
                      )}

                      {interaction.orderNumber && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <DescriptionIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                          <Link
                            href={`${momentumUrl}new-home/${interaction.orderNumber}`}
                            target="_blank"
                            underline="hover"
                            sx={{ display: "inline-flex", alignItems: "center" }}
                          >
                            <Typography variant="caption" fontWeight={500}>
                              {interaction.orderNumber}
                            </Typography>
                          </Link>
                        </Box>
                      )}

                      {interaction.event?.name && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <EventIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                          <Typography variant="caption" fontWeight={500}>
                            {interaction.event.name}
                          </Typography>
                        </Box>
                      )}

                      {interaction.notes && (
                        <Typography
                          variant="caption"
                          sx={{
                            whiteSpace: "pre-wrap",
                            color: theme.palette.text.primary,
                            lineHeight: 1.5
                          }}
                        >
                          {interaction.notes}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </DialogContent>
    </Dialog>
  );
};
